import "@/index.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CityProvider } from "./context/CityContext";
import { Toaster } from "./components/ui/sonner";
import LocationPermissionPrompt from "./components/LocationPermissionPrompt";
import InstallAppBanner from "./components/InstallAppBanner";
import UpdateBanner from "./components/UpdateBanner";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages (loaded on demand to keep startup fast)
import AuthPage from "./pages/AuthPage";
const pageLoaders = {
  ClientDashboard: () => import("./pages/ClientDashboard"),
  ClientProfilePage: () => import("./pages/ClientProfilePage"),
  ClientOnboardingPage: () => import("./pages/ClientOnboardingPage"),
  StaffOnboardingPage: () => import("./pages/StaffOnboardingPage"),
  WalkerDashboard: () => import("./pages/WalkerDashboard"),
  WalkerSchedulePage: () => import("./pages/WalkerSchedulePage"),
  WalkerMySchedulePage: () => import("./pages/WalkerMySchedulePage"),
  SitterDashboard: () => import("./pages/SitterDashboard"),
  SitterProfilePage: () => import("./pages/SitterProfilePage"),
  AdminDashboard: () => import("./pages/AdminDashboard"),
  SchedulePage: () => import("./pages/SchedulePage"),
  BillingPage: () => import("./pages/BillingPage"),
  PetsPage: () => import("./pages/PetsPage"),
  MessagesPage: () => import("./pages/MessagesPage"),
  CalendarPage: () => import("./pages/CalendarPage"),
  PayrollPage: () => import("./pages/PayrollPage"),
  WalkerProfilePage: () => import("./pages/WalkerProfilePage"),
  AdminClientsPage: () => import("./pages/AdminClientsPage"),
  AdminWalkersPage: () => import("./pages/AdminWalkersPage"),
  AdminSittersPage: () => import("./pages/AdminSittersPage"),
  AdminInvoicesPage: () => import("./pages/AdminInvoicesPage"),
  AdminPayrollPage: () => import("./pages/AdminPayrollPage"),
  AdminOvernightsPage: () => import("./pages/AdminOvernightsPage"),
  AdminDaycareCalendarPage: () => import("./pages/AdminDaycareCalendarPage"),
  AdminTimeOffPage: () => import("./pages/AdminTimeOffPage"),
  AdminProfilePage: () => import("./pages/AdminProfilePage"),
  AdminBoardingLocationsPage: () => import("./pages/AdminBoardingLocationsPage"),
  AdminBoardingSchedulePage: () => import("./pages/AdminBoardingSchedulePage"),
  LiveTrackingPage: () => import("./pages/LiveTrackingPage"),
  MassTextPage: () => import("./pages/MassTextPage"),
  SubscriptionPage: () => import("./pages/SubscriptionPage"),
  SetupPage: () => import("./pages/SetupPage"),
  DogParkPage: () => import("./pages/DogParkPage"),
  PublicInvoicePage: () => import("./pages/PublicInvoicePage"),
  WalkReportPage: () => import("./pages/WalkReportPage"),
  SMSInboxPage: () => import("./pages/SMSInboxPage"),
  RegisterPage: () => import("./pages/RegisterPage"),
};
const ClientDashboard = lazy(pageLoaders.ClientDashboard);
const ClientProfilePage = lazy(pageLoaders.ClientProfilePage);
const ClientOnboardingPage = lazy(pageLoaders.ClientOnboardingPage);
const StaffOnboardingPage = lazy(pageLoaders.StaffOnboardingPage);
const WalkerDashboard = lazy(pageLoaders.WalkerDashboard);
const WalkerSchedulePage = lazy(pageLoaders.WalkerSchedulePage);
const WalkerMySchedulePage = lazy(pageLoaders.WalkerMySchedulePage);
const SitterDashboard = lazy(pageLoaders.SitterDashboard);
const SitterProfilePage = lazy(pageLoaders.SitterProfilePage);
const AdminDashboard = lazy(pageLoaders.AdminDashboard);
const SchedulePage = lazy(pageLoaders.SchedulePage);
const BillingPage = lazy(pageLoaders.BillingPage);
const PetsPage = lazy(pageLoaders.PetsPage);
const MessagesPage = lazy(pageLoaders.MessagesPage);
const CalendarPage = lazy(pageLoaders.CalendarPage);
const PayrollPage = lazy(pageLoaders.PayrollPage);
const WalkerProfilePage = lazy(pageLoaders.WalkerProfilePage);
const AdminClientsPage = lazy(pageLoaders.AdminClientsPage);
const AdminWalkersPage = lazy(pageLoaders.AdminWalkersPage);
const AdminSittersPage = lazy(pageLoaders.AdminSittersPage);
const AdminInvoicesPage = lazy(pageLoaders.AdminInvoicesPage);
const AdminPayrollPage = lazy(pageLoaders.AdminPayrollPage);
const AdminOvernightsPage = lazy(pageLoaders.AdminOvernightsPage);
const AdminDaycareCalendarPage = lazy(pageLoaders.AdminDaycareCalendarPage);
const AdminTimeOffPage = lazy(pageLoaders.AdminTimeOffPage);
const AdminProfilePage = lazy(pageLoaders.AdminProfilePage);
const AdminBoardingLocationsPage = lazy(pageLoaders.AdminBoardingLocationsPage);
const AdminBoardingSchedulePage = lazy(pageLoaders.AdminBoardingSchedulePage);
const LiveTrackingPage = lazy(pageLoaders.LiveTrackingPage);
const MassTextPage = lazy(pageLoaders.MassTextPage);
const SubscriptionPage = lazy(pageLoaders.SubscriptionPage);
const SetupPage = lazy(pageLoaders.SetupPage);
const DogParkPage = lazy(pageLoaders.DogParkPage);
const PublicInvoicePage = lazy(pageLoaders.PublicInvoicePage);
const WalkReportPage = lazy(pageLoaders.WalkReportPage);
const SMSInboxPage = lazy(pageLoaders.SMSInboxPage);
const RegisterPage = lazy(pageLoaders.RegisterPage);

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Warm the cache with every screen after startup so they open instantly and work offline
const prefetchPages = () => Object.values(pageLoaders).forEach((load) => load().catch(() => {}));

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role (without replace to preserve history)
    switch (user?.role) {
      case 'admin':
        return <Navigate to="/admin" />;
      case 'walker':
        return <Navigate to="/walker" />;
      default:
        return <Navigate to="/dashboard" />;
    }
  }

  return children;
};

// Home redirect based on auth state
const HomeRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Don't use replace here so back button works properly
  switch (user?.role) {
    case 'admin':
      return <Navigate to="/admin" />;
    case 'walker':
      return <Navigate to="/walker" />;
    case 'sitter':
      return <Navigate to="/sitter" />;
    default:
      return <Navigate to="/dashboard" />;
  }
};

// Legacy invoice redirect component
const LegacyInvoiceRedirect = () => {
  const { invoiceId } = useParams();
  return <Navigate to={`/invoice/${invoiceId}`} replace />;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Home redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Setup - First time admin registration */}
      <Route path="/setup" element={<SetupPage />} />
      
      {/* Registration - New customer or team application */}
      <Route path="/register" element={<RegisterPage />} />

      {/* Auth */}
      <Route
        path="/auth"
        element={isAuthenticated ? <HomeRedirect /> : <AuthPage />}
      />

      {/* Public Invoice View - No authentication required */}
      <Route path="/invoice/:invoiceId" element={<PublicInvoicePage />} />

      {/* Walk Report - Protected route */}
      <Route
        path="/walk-report/:appointmentId"
        element={
          <ProtectedRoute allowedRoles={['admin', 'walker', 'client']}>
            <WalkReportPage />
          </ProtectedRoute>
        }
      />

      {/* Client Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      {/* Legacy client invoice route - redirect to public invoice */}
      <Route
        path="/client/invoice/:invoiceId"
        element={<LegacyInvoiceRedirect />}
      />
      <Route
        path="/pets"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <PetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/onboarding"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tracking"
        element={
          <ProtectedRoute allowedRoles={['client', 'walker', 'admin']}>
            <LiveTrackingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute allowedRoles={['client', 'walker', 'admin']}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />

      {/* Walker Routes */}
      <Route
        path="/walker"
        element={
          <ProtectedRoute allowedRoles={['walker']}>
            <WalkerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/walker/onboarding"
        element={
          <ProtectedRoute allowedRoles={['walker']}>
            <StaffOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/walker/schedule"
        element={
          <ProtectedRoute allowedRoles={['walker']}>
            <WalkerMySchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/walker/schedule/new"
        element={
          <ProtectedRoute allowedRoles={['walker']}>
            <WalkerSchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/walker/payroll"
        element={
          <ProtectedRoute allowedRoles={['walker']}>
            <PayrollPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/walker/chat"
        element={
          <ProtectedRoute allowedRoles={['walker']}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/walker/profile"
        element={
          <ProtectedRoute allowedRoles={['walker']}>
            <WalkerProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Sitter Routes */}
      <Route
        path="/sitter"
        element={
          <ProtectedRoute allowedRoles={['sitter']}>
            <SitterDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sitter/onboarding"
        element={
          <ProtectedRoute allowedRoles={['sitter']}>
            <StaffOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sitter/schedule"
        element={
          <ProtectedRoute allowedRoles={['sitter']}>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sitter/payroll"
        element={
          <ProtectedRoute allowedRoles={['sitter']}>
            <PayrollPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sitter/chat"
        element={
          <ProtectedRoute allowedRoles={['sitter']}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sitter/profile"
        element={
          <ProtectedRoute allowedRoles={['sitter']}>
            <SitterProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/calendar"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/walkers"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminWalkersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sitters"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSittersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payroll"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPayrollPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/overnights"
        element={
          <ProtectedRoute allowedRoles={['admin', 'walker']}>
            <AdminOvernightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/daycare"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDaycareCalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/time-off"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminTimeOffPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sms-inbox"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SMSInboxPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/boarding-locations"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminBoardingLocationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/boarding-locations/:locationId/schedule"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminBoardingSchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminInvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminInvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/chat"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/mass-text"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <MassTextPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />

      {/* Dog Park - Available to all authenticated users */}
      <Route
        path="/dog-park"
        element={
          <ProtectedRoute allowedRoles={['admin', 'walker', 'sitter', 'client']}>
            <DogParkPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Location is only needed by staff who do walks/visits; never shown to clients or on invoice links
const StaffLocationPrompt = () => {
  const { user } = useAuth();
  const isStaff = user && (['admin', 'walker', 'sitter'].includes(user.role) || user.is_walker || user.is_sitter);
  return isStaff ? <LocationPermissionPrompt /> : null;
};

function App() {
  useEffect(() => {
    const t = setTimeout(prefetchPages, 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <CityProvider>
            <StaffLocationPrompt />
            <InstallAppBanner />
            <UpdateBanner />
            <Suspense fallback={<PageLoader />}>
              <AppRoutes />
            </Suspense>
            <Toaster richColors position="top-right" />
          </CityProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
